import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Switch,
  Popconfirm,
  Select,
  Segmented,
  Checkbox,
  Card,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  DatabaseOutlined,
  FileSearchOutlined,
  NumberOutlined,
  IdcardOutlined,
  TagOutlined,
  CalendarOutlined,
  BankOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  UserOutlined,
  EyeOutlined,
  CloseOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import axios from "../../services/axiosInstance";
import type { ColumnType, TableProps } from "antd/es/table";
import type { FilterValue } from "antd/es/table/interface";
import {
  DELETE_DOCUMENT,
  FETCH_ALL_DOCUMENTS,
  FETCH_SIGNED_DOCUMENTS,
  TOGGLE_DOCUMENT_AS_APPLIED,
  TOGGLE_REMINDER_EMAIL_AS_RENEWED,
} from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";
import {
  CATEGORY_LABEL,
  DocumentCategory,
  matchesCategory,
} from "./documentCategory";
import { toastSuccess, toastError, toastWarning } from "../../utils/toast";

const { Title, Text } = Typography;

export const isPdf = (url: string) => url.toLowerCase().includes(".pdf");

/* ─── Expiry date cell ─────────────────────────────────── */
const renderExpiryDate = (date?: string) => {
  if (!date) return <Text type="secondary">-</Text>;
  const daysLeft = moment(date).diff(moment().startOf("day"), "days");
  let color = "default";
  let label = "";
  if (daysLeft < 0) {
    color = "red";
    label = "Expired";
  } else if (daysLeft <= 30) {
    color = "orange";
    label = `${daysLeft}d left`;
  } else if (daysLeft <= 60) {
    color = "gold";
    label = `${daysLeft}d left`;
  }
  return (
    <Space size={4} direction="vertical" style={{ lineHeight: 1 }}>
      <Tag
        color={color}
        style={{ fontWeight: 500, fontSize: 12, padding: "2px 8px" }}
      >
        {moment(date).format("MMM D, YYYY")}
      </Tag>
      {label && (
        <Text
          style={{
            fontSize: 11,
            color:
              color === "red"
                ? "#ef4444"
                : color === "orange"
                ? "#f59e0b"
                : "#eab308",
            fontWeight: 600,
          }}
        >
          {label}
        </Text>
      )}
    </Space>
  );
};

/* ─── Stat card config ─────────────────────────────────── */
const STAT_CARDS = [
  {
    key: "total",
    label: "Total Documents",
    icon: <DatabaseOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "renewed",
    label: "Renewed",
    icon: <CheckCircleOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "applied",
    label: "Applied",
    icon: <SyncOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
  {
    key: "pending",
    label: "Pending Renewal",
    icon: <FileTextOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
  },
];

interface DocumentTableComponentProps {
  category?: DocumentCategory;
}

const DocumentTableComponent = ({ category }: DocumentTableComponentProps = {}) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState<IDatabase[]>([]);
  const searchInput = useRef<InputRef>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<
    Record<string, FilterValue | null>
  >({});
  const [tableSize, setTableSize] = useState<TableProps["size"]>("middle");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"list" | "vehicle">("list");

  /* ---------------- API ---------------- */

  const fetchSignedUrl = async (s3Url: string) => {
    const response = await axios.get(
      FETCH_SIGNED_DOCUMENTS + `?s3Url=${encodeURIComponent(s3Url)}`
    );
    if (response?.data?.results) return response.data.results;
    throw new Error("Failed to fetch signed URL");
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(FETCH_ALL_DOCUMENTS);
      if (response?.data?.results) setData(response.data.results);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const downloadVisibleTableAsCSV = () => {
    if (!filteredData.length) {
      toastWarning("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Document No",
      "Document Type",
      "Expiry Date",
      "Company Name",
      "Location",
      "Details",
      "Uploaded By",
      "Renewed",
      "Applied",
    ];

    const rows = filteredData.map((item) => {
      const meta =
        item.meta && typeof item.meta === "object"
          ? Object.entries(item.meta)
              .map(([k, v]) => `${snakeToCapitalCase(k)}: ${v}`)
              .join(" | ")
          : "";

      return [
        String(item.sNo ?? ""),
        String(item.uniqId ?? ""),
        snakeToCapitalCase(String(item.documentType ?? "")),
        item.expiryDate ? moment(item.expiryDate).format("YYYY-MM-DD") : "",
        String(item.companyName ?? ""),
        String(item.location ?? ""),
        String(meta),
        String(item.uploadedBy ?? ""),
        item.isRenewed ? "Yes" : "No",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `documents_${moment().format("YYYY_MM_DD_HH_mm")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
    toastSuccess("CSV downloaded");
  };

  /* ---------------- Actions ---------------- */

  const handleRenewToggle = useCallback(async (record: IDatabase) => {
    try {
      if (!record.isRenewed) {
        await axios.put(
          TOGGLE_REMINDER_EMAIL_AS_RENEWED(record.documentId),
          {}
        );
        toastSuccess("Renewal reminder set");
        fetchDocuments();
      }
    } catch {
      toastError("Failed to update renewal status");
    }
  }, []);

  const handlePreview = useCallback(async (s3Url: string) => {
    setLoadingPreview(true);
    try {
      const url = await fetchSignedUrl(s3Url);
      setSignedUrl(url);
      setIsPreviewOpen(true);
    } catch {
      toastError("Failed to load document");
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const handleDelete = useCallback(async (record: IDatabase) => {
    try {
      await axios.delete(DELETE_DOCUMENT(record.documentId));
      toastSuccess("Document deleted");
      fetchDocuments();
    } catch {
      toastError("Failed to delete document");
    }
  }, []);

  /* ---------------- Column Search ---------------- */

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 250),
    []
  );

  const getColumnSearchProps = useCallback(
    (dataIndex: keyof IDatabase): ColumnType<IDatabase> => ({
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8, width: 240 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${String(dataIndex)}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ marginBottom: 8 }}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<SearchOutlined />}
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            >
              Search
            </Button>
            <Button size="small" onClick={() => clearFilters?.()}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record[dataIndex]
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()) || false,
      render: (text: string) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text || ""}
          />
        ) : (
          <span style={{ whiteSpace: "nowrap" }}>{text}</span>
        ),
    }),
    [searchText, searchedColumn, handleSearch]
  );

  const handleAppliedToggle = useCallback(async (record: IDatabase, checked: boolean) => {
    setData((prev) =>
      prev.map((item) =>
        item.documentId === record.documentId
          ? { ...item, isApplied: checked }
          : item
      )
    );
    await axios.put(TOGGLE_DOCUMENT_AS_APPLIED(record.documentId), {});
    toastSuccess("Document Applied");
    fetchDocuments();
  }, []);

  /* ---------------- Select Options ---------------- */

  const categoryScopedData = useMemo(
    () =>
      category
        ? data.filter((d) => matchesCategory(d.documentType, category))
        : data,
    [data, category]
  );

  const documentTypeOptions = useMemo(() => {
    const unique = Array.from(
      new Set(categoryScopedData.map((d) => d.documentType).filter(Boolean))
    );
    return unique.map((type) => ({
      label: snakeToCapitalCase(type as string),
      value: type as string,
    }));
  }, [categoryScopedData]);

  const locationOptions = useMemo(() => {
    const unique = Array.from(
      new Set(categoryScopedData.map((d) => d.location).filter(Boolean))
    );
    return unique.map((loc) => ({
      label: loc as string,
      value: loc as string,
    }));
  }, [categoryScopedData]);

  const filteredData = useMemo(() => {
    return categoryScopedData.filter((item) => {
      const typeMatch =
        selectedDocTypes.length === 0 ||
        selectedDocTypes.includes(item.documentType as string);

      const locationMatch =
        selectedLocations.length === 0 ||
        selectedLocations.includes(item.location as string);

      const globalMatch =
        !globalSearch ||
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalSearch.toLowerCase());

      const columnMatch = Object.entries(columnFilters).every(
        ([dataIndex, values]) =>
          !values?.length ||
          values.some((value) =>
            item[dataIndex as keyof IDatabase]
              ?.toString()
              .toLowerCase()
              .includes(String(value).toLowerCase())
          )
      );

      return typeMatch && locationMatch && globalMatch && columnMatch;
    });
  }, [
    categoryScopedData,
    selectedDocTypes,
    selectedLocations,
    globalSearch,
    columnFilters,
  ]);

  /* ---------------- Group by Vehicle ---------------- */

  const vehicleRows = useMemo(() => {
    const groups = new Map<string, IDatabase[]>();
    for (const item of filteredData) {
      const meta = item.meta as Record<string, unknown> | undefined;
      const vehicleNo = meta?.vehicle_number;
      if (typeof vehicleNo !== "string" || !vehicleNo.trim()) continue;
      const key = vehicleNo.trim();
      const existing = groups.get(key);
      if (existing) existing.push(item);
      else groups.set(key, [item]);
    }

    const earliestExpiry = (docs: IDatabase[], keyword: string) => {
      const dates = docs
        .filter((d) => d.documentType?.toLowerCase().includes(keyword))
        .map((d) => d.expiryDate)
        .filter((d): d is string => !!d);
      if (!dates.length) return undefined;
      return dates.reduce((a, b) =>
        moment(a).unix() <= moment(b).unix() ? a : b
      );
    };

    return Array.from(groups.entries()).map(([vehicleNo, docs]) => ({
      vehicleNo,
      rcCopy: docs.find((d) =>
        d.documentType?.toLowerCase().includes("registration")
      ),
      permitExpiry: earliestExpiry(docs, "permit"),
      roadTaxExpiry: earliestExpiry(docs, "road"),
      fitnessExpiry: earliestExpiry(docs, "fitness"),
      insuranceExpiry: earliestExpiry(docs, "insurance"),
      pollutionExpiry: earliestExpiry(docs, "pollution"),
    }));
  }, [filteredData]);

  /* ---------------- Stats ---------------- */

  const stats = useMemo(() => ({
    total: categoryScopedData.length,
    renewed: categoryScopedData.filter((d) => d.isRenewed).length,
    applied: categoryScopedData.filter((d) => d.isApplied).length,
    pending: categoryScopedData.filter((d) => !d.isRenewed).length,
  }), [categoryScopedData]);

  /* ---------------- Columns ---------------- */

  const columns = useMemo<ColumnType<IDatabase>[]>(
    () => [
      {
        title: <><NumberOutlined style={{ fontSize: 12 }} /> #</>,
        key: "sNo",
        dataIndex: "sNo",
        width: 60,
        align: "center",
        render: (value: number) => (
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 500 }}>
            {value}
          </Text>
        ),
      },
      {
        title: <><IdcardOutlined style={{ fontSize: 12 }} /> Document No</>,
        dataIndex: "uniqId",
        key: "uniqId",
        width: 170,
        render: (value?: string) =>
          value ? (
            <Text strong style={{ fontSize: 13, color: "#1e293b" }}>
              {value}
            </Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><TagOutlined style={{ fontSize: 12 }} /> Type</>,
        dataIndex: "documentType",
        key: "documentType",
        sorter: (a, b) =>
          (a.documentType || "").localeCompare(b.documentType || ""),
        ...getColumnSearchProps("documentType"),
        render: (text: string) => {
          if (!text) return <Text type="secondary">-</Text>;
          return (
            <Tag
              style={{
                fontWeight: 500,
                fontSize: 12,
                padding: "2px 10px",
                borderRadius: 6,
              }}
            >
              {snakeToCapitalCase(text)}
            </Tag>
          );
        },
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Expiry Date</>,
        dataIndex: "expiryDate",
        width: 170,
        sorter: (a, b) =>
          moment(a.expiryDate).unix() - moment(b.expiryDate).unix(),
        render: (date) => renderExpiryDate(date),
      },
      {
        title: <><BankOutlined style={{ fontSize: 12 }} /> Company</>,
        dataIndex: "companyName",
        key: "companyName",
        sorter: (a, b) =>
          (a.companyName || "").localeCompare(b.companyName || ""),
        ...getColumnSearchProps("companyName"),
        render: (value?: string) =>
          value ? (
            <Space size={6}>
              <BankOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
              <Text style={{ fontSize: 13 }}>{value}</Text>
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><EnvironmentOutlined style={{ fontSize: 12 }} /> Location</>,
        dataIndex: "location",
        key: "location",
        sorter: (a, b) => (a.location || "").localeCompare(b.location || ""),
        ...getColumnSearchProps("location"),
        render: (location?: string) =>
          location ? (
            <Space size={6}>
              <EnvironmentOutlined style={{ color: "#94a3b8", fontSize: 13 }} />
              <Text style={{ fontSize: 13 }}>{location}</Text>
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><InfoCircleOutlined style={{ fontSize: 12 }} /> Details</>,
        dataIndex: "meta",
        width: 380,
        render: (meta) => {
          if (!meta) return "-";
          if (typeof meta === "object" && !Array.isArray(meta)) {
            return imageInfo(meta).map(({ key, value }) => (
              <Tag
                key={key}
                color="blue"
                style={{ marginBottom: 2, marginRight: 2, fontSize: 10 }}
              >
                <b>{snakeToCapitalCase(key)}</b>: {String(value)}
              </Tag>
            ));
          }
          if (Array.isArray(meta)) {
            return meta.map((item) => (
              <Tag key={item} color="blue">
                {item}
              </Tag>
            ));
          }
          return "-";
        },
      },
      {
        title: <><UserOutlined style={{ fontSize: 12 }} /> Uploaded By</>,
        dataIndex: "uploadedBy",
        width: 140,
        ellipsis: true,
        render: (value?: string) =>
          value ? (
            <Text style={{ fontSize: 13 }}>{value}</Text>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><EyeOutlined style={{ fontSize: 12 }} /> File</>,
        dataIndex: "s3Url",
        width: 80,
        align: "center",
        render: (s3Url: string) =>
          s3Url ? (
            <Button
              type="link"
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => handlePreview(s3Url)}
              style={{ fontSize: 12, padding: "0 4px" }}
            >
              View
            </Button>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><CheckCircleOutlined style={{ fontSize: 12 }} /> Renewed</>,
        dataIndex: "isRenewed",
        align: "center",
        render: (_, record) => (
          <Switch
            checked={record.isRenewed}
            size="small"
            onChange={() => handleRenewToggle(record)}
          />
        ),
      },
      {
        title: <><SyncOutlined style={{ fontSize: 12 }} /> Applied</>,
        dataIndex: "isApplied",
        align: "center",
        render: (_, record) => (
          <Checkbox
            checked={!!record.isApplied}
            onChange={(e) => handleAppliedToggle(record, e.target.checked)}
          />
        ),
      },
      {
        title: <><DeleteOutlined style={{ fontSize: 12 }} /> Action</>,
        align: "center",
        width: 70,
        render: (_, record) => (
          <Popconfirm
            title="Delete this document?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okType="danger"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              style={{ borderRadius: 6 }}
            />
          </Popconfirm>
        ),
      },
    ],
    [
      getColumnSearchProps,
      handleAppliedToggle,
      handleDelete,
      handleRenewToggle,
      handlePreview,
    ]
  );

  /* ---------------- Vehicle Columns ---------------- */

  type VehicleRow = (typeof vehicleRows)[number];

  const vehicleColumns = useMemo<ColumnType<VehicleRow>[]>(
    () => [
      {
        title: <><IdcardOutlined style={{ fontSize: 12 }} /> Vehicle No.</>,
        dataIndex: "vehicleNo",
        key: "vehicleNo",
        width: 160,
        fixed: "left",
        sorter: (a, b) => a.vehicleNo.localeCompare(b.vehicleNo),
        render: (value: string) => (
          <Text strong style={{ fontSize: 13, color: "#1e293b" }}>
            {value}
          </Text>
        ),
      },
      {
        title: <><EyeOutlined style={{ fontSize: 12 }} /> RC Copy</>,
        dataIndex: "rcCopy",
        key: "rcCopy",
        width: 100,
        align: "center",
        render: (rcCopy?: IDatabase) =>
          rcCopy?.s3Url ? (
            <Button
              type="link"
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => handlePreview(rcCopy.s3Url)}
              style={{ fontSize: 12, padding: "0 4px" }}
            >
              View
            </Button>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Permit Expiry Date</>,
        dataIndex: "permitExpiry",
        key: "permitExpiry",
        width: 160,
        render: (date?: string) => renderExpiryDate(date),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Road Tax Validity</>,
        dataIndex: "roadTaxExpiry",
        key: "roadTaxExpiry",
        width: 160,
        render: (date?: string) => renderExpiryDate(date),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Fitness Expiry Date</>,
        dataIndex: "fitnessExpiry",
        key: "fitnessExpiry",
        width: 160,
        render: (date?: string) => renderExpiryDate(date),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Insurance Expiry Date</>,
        dataIndex: "insuranceExpiry",
        key: "insuranceExpiry",
        width: 160,
        render: (date?: string) => renderExpiryDate(date),
      },
      {
        title: <><CalendarOutlined style={{ fontSize: 12 }} /> Pollution Expiry Date</>,
        dataIndex: "pollutionExpiry",
        key: "pollutionExpiry",
        width: 160,
        render: (date?: string) => renderExpiryDate(date),
      },
    ],
    [handlePreview]
  );

  /* ---------------- UI ---------------- */

  return (
    <div>
      {/* Page header */}
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <DatabaseOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            {category
              ? `${CATEGORY_LABEL[category]} Documents`
              : "Document Directory"}
          </Title>
          <Text className="page-subtitle">
            {category === "clientwise"
              ? "Licenses & client-level documents (FSSAI, Trade, CLRA, Repacker, Shop & Establishment)"
              : category === "vehiclewise"
              ? "Vehicle-level documents (Insurance, Permit, Driving License, Pollution, Fitness, Registration, Road Tax)"
              : "Manage and track all uploaded documents"}
          </Text>
        </Col>
      </Row>

      {/* Stat cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={12} sm={12} md={6}>
            <Card
              className="stat-card"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
              }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.1,
                    }}
                  >
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* View tabs (vehiclewise only) */}
      {category === "vehiclewise" && (
        <Segmented
          style={{ marginBottom: 16 }}
          options={[
            { label: "Document List", value: "list", icon: <DatabaseOutlined /> },
            { label: "Group by Vehicle", value: "vehicle", icon: <IdcardOutlined /> },
          ]}
          value={viewMode}
          onChange={(v) => setViewMode(v as "list" | "vehicle")}
        />
      )}

      {/* Toolbar + Table */}
      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search all fields..."
              style={{ width: 240 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by location"
              style={{ minWidth: 180 }}
              options={locationOptions}
              value={selectedLocations}
              onChange={setSelectedLocations}
              maxTagCount="responsive"
              suffixIcon={<EnvironmentOutlined style={{ color: "#94a3b8" }} />}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Filter by type"
              style={{ minWidth: 180 }}
              options={documentTypeOptions}
              value={selectedDocTypes}
              onChange={setSelectedDocTypes}
              maxTagCount="responsive"
              suffixIcon={<FilterOutlined style={{ color: "#94a3b8" }} />}
            />
            <Segmented
              size="small"
              options={[
                { label: "Compact", value: "small" },
                { label: "Default", value: "middle" },
                { label: "Large", value: "large" },
              ]}
              value={tableSize}
              onChange={(v) => setTableSize(v as TableProps["size"])}
            />
          </Space>
          <Space size={8}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSelectedDocTypes([]);
                setSelectedLocations([]);
                setGlobalSearch("");
                setColumnFilters({});
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={downloadVisibleTableAsCSV}
              disabled={filteredData.length === 0}
            >
              Export CSV
            </Button>
          </Space>
        </div>

        {viewMode === "vehicle" && category === "vehiclewise" ? (
          <Table
            rowKey="vehicleNo"
            size={tableSize}
            columns={vehicleColumns}
            dataSource={vehicleRows}
            loading={{
              spinning: loading,
              tip: "Loading documents...",
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "40px 0" }}>
                  <IdcardOutlined
                    style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                  />
                  <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                    No vehicles found
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                    {globalSearch || selectedDocTypes.length || selectedLocations.length
                      ? "Try adjusting your filters"
                      : "Upload a vehicle document to get started"}
                  </div>
                </div>
              ),
            }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => (
                <Text style={{ color: "#64748b", fontSize: 13 }}>
                  Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                  <Text strong>{total}</Text> vehicle{total !== 1 ? "s" : ""}
                </Text>
              ),
            }}
            sticky
            scroll={{ x: "max-content" }}
          />
        ) : (
          <Table
            rowKey="documentId"
            size={tableSize}
            columns={columns}
            dataSource={filteredData}
            onChange={(_pagination, filters) => setColumnFilters(filters)}
            loading={{
              spinning: loading,
              tip: "Loading documents...",
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "40px 0" }}>
                  <DatabaseOutlined
                    style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                  />
                  <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                    No documents found
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                    {globalSearch || selectedDocTypes.length || selectedLocations.length
                      ? "Try adjusting your filters"
                      : "Upload a document to get started"}
                  </div>
                </div>
              ),
            }}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => (
                <Text style={{ color: "#64748b", fontSize: 13 }}>
                  Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                  <Text strong>{total}</Text> document{total !== 1 ? "s" : ""}
                </Text>
              ),
            }}
            sticky
            scroll={{ x: "max-content" }}
          />
        )}
      </div>

      <Modal
        open={isPreviewOpen}
        title={<><EyeOutlined style={{ marginRight: 8, color: "#1677ff" }} />Document Preview</>}
        onCancel={() => {
          setIsPreviewOpen(false);
          setSignedUrl(null);
        }}
        width="80%"
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            disabled={!signedUrl}
            onClick={() => {
              const link = document.createElement("a");
              link.href = signedUrl || "";
              link.download = "";
              link.target = "_blank";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>,
          <Button
            key="close"
            icon={<CloseOutlined />}
            onClick={() => {
              setIsPreviewOpen(false);
              setSignedUrl(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        {loadingPreview && !signedUrl ? (
          <div style={{ textAlign: "center", padding: 40 }}>Loading...</div>
        ) : signedUrl && isPdf(signedUrl) ? (
          <iframe
            src={signedUrl}
            width="100%"
            style={{ height: "75vh" }}
            title="Document Preview"
          />
        ) : (
          <img
            src={signedUrl || ""}
            alt="Document Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              display: "block",
              margin: "0 auto",
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default DocumentTableComponent;
